import React, { useMemo } from "react"
import { Table, Tag, Tooltip, Typography, Spin } from "@mimber/ui-web"
import type { ColumnsType } from "@mimber/ui-web"
import {
  ASSIGNABLE_RESOURCES,
  RESOURCE_LABELS,
  STANDARD_ACTIONS,
  SEND_RESOURCES,
  Action,
} from "@mimber/schema"
import type {
  PermissionMatrixPreviewProps,
  MatrixRow,
  PermissionSource,
} from "./PermissionMatrixPreview.types"

const { Text } = Typography

const ACTION_LABELS: Record<string, string> = {
  READ: "Read",
  CREATE: "Create",
  UPDATE: "Update",
  DELETE: "Delete",
  MANAGE: "Manage",
  SEND: "Send",
}

const SOURCE_COLORS: Record<PermissionSource["type"], string> = {
  direct: "green",
  role: "blue",
  group: "purple",
}

const SOURCE_LABELS: Record<PermissionSource["type"], string> = {
  direct: "Direct",
  role: "Role",
  group: "Group",
}

/**
 * Builds a tooltip string listing all sources for a permission.
 */
const buildTooltip = (sources: PermissionSource[]): string =>
  sources
    .map((s) => {
      if (s.type === "direct") return "Direct permission"
      if (s.type === "role") return `Role: ${s.roleName}`
      return `Group: ${s.groupName} → ${s.roleName}`
    })
    .join("\n")

/**
 * Returns the highest-priority source type for display color.
 * Priority: direct > role > group
 */
const primarySourceType = (
  sources: PermissionSource[]
): PermissionSource["type"] => {
  if (sources.some((s) => s.type === "direct")) return "direct"
  if (sources.some((s) => s.type === "role")) return "role"
  return "group"
}

/**
 * PermissionMatrixPreview — A read-only matrix showing effective permissions
 * for a member across all assignable resources and actions, with color-coded
 * indicators based on permission source.
 */
export const PermissionMatrixPreview: React.FunctionComponent<
  PermissionMatrixPreviewProps
> = ({ directPermissions, directRoles, groupInheritedRoles, isLoading }) => {
  /** Build the aggregate permission map */
  const rows: MatrixRow[] = useMemo(() => {
    // Build a map: "RESOURCE:ACTION" → PermissionSource[]
    const sourceMap = new Map<string, PermissionSource[]>()

    const addSource = (
      resource: string,
      action: string,
      source: PermissionSource
    ) => {
      const key = `${resource}:${action}`
      const existing = sourceMap.get(key) ?? []
      existing.push(source)
      sourceMap.set(key, existing)
    }

    // Direct permissions
    for (const perm of directPermissions) {
      if (perm.granted) {
        addSource(perm.resource, perm.action, { type: "direct" })
      }
    }

    // Direct role permissions
    for (const assignment of directRoles) {
      const roleName = assignment.organizationRole.name
      for (const perm of assignment.organizationRole.permissions ?? []) {
        addSource(perm.resource, perm.action, { type: "role", roleName })
      }
    }

    // Group-inherited role permissions
    for (const assignment of groupInheritedRoles) {
      const roleName = assignment.organizationRole.name
      const groupName = assignment.group.name
      for (const perm of assignment.organizationRole.permissions ?? []) {
        addSource(perm.resource, perm.action, {
          type: "group",
          groupName,
          roleName,
        })
      }
    }

    return ASSIGNABLE_RESOURCES.map((resource) => {
      const actions: Record<string, PermissionSource[]> = {}

      for (const action of STANDARD_ACTIONS) {
        actions[action] = sourceMap.get(`${resource}:${action}`) ?? []
      }
      // SEND action
      if ((SEND_RESOURCES as readonly string[]).includes(resource)) {
        actions[Action.SEND] = sourceMap.get(`${resource}:${Action.SEND}`) ?? []
      }

      return {
        key: resource,
        resource,
        label: RESOURCE_LABELS[resource],
        supportsSend: (SEND_RESOURCES as readonly string[]).includes(resource),
        actions,
      }
    })
  }, [directPermissions, directRoles, groupInheritedRoles])

  /** Count total active permissions */
  const activeCount = useMemo(
    () =>
      rows.reduce(
        (sum, row) =>
          sum + Object.values(row.actions).filter((s) => s.length > 0).length,
        0
      ),
    [rows]
  )

  const renderCell = (actionKey: string, record: MatrixRow) => {
    if (actionKey === "SEND" && !record.supportsSend) {
      return <Text type="secondary">—</Text>
    }

    const sources = record.actions[actionKey] ?? []
    if (sources.length === 0) {
      return <Text type="secondary">—</Text>
    }

    const primary = primarySourceType(sources)
    return (
      <Tooltip
        title={buildTooltip(sources)}
        overlayStyle={{ whiteSpace: "pre-line" }}
      >
        <Tag
          color={SOURCE_COLORS[primary]}
          data-testid={`matrix-${record.resource}-${actionKey}`}
        >
          {SOURCE_LABELS[primary]}
        </Tag>
      </Tooltip>
    )
  }

  const columns: ColumnsType<MatrixRow> = useMemo(
    () => [
      {
        title: "Resource",
        dataIndex: "label",
        key: "resource",
        width: 180,
      },
      ...STANDARD_ACTIONS.map((action) => ({
        title: <Text type="secondary">{ACTION_LABELS[action]}</Text>,
        key: action,
        align: "center" as const,
        width: 90,
        render: (_: unknown, record: MatrixRow) => renderCell(action, record),
      })),
      {
        title: <Text type="secondary">Send</Text>,
        key: "SEND",
        align: "center" as const,
        width: 90,
        render: (_: unknown, record: MatrixRow) => renderCell("SEND", record),
      },
    ],
    [rows]
  )

  if (isLoading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: 24 }}
        data-testid="matrix-loading"
      >
        <Spin />
      </div>
    )
  }

  return (
    <div data-testid="permission-matrix-preview">
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary">
          {activeCount} active permission{activeCount !== 1 ? "s" : ""} across
          all sources
        </Text>
      </div>

      <Table<MatrixRow>
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="small"
        bordered
      />

      <div
        style={{
          display: "flex",
          gap: 16,
          marginTop: 8,
        }}
        data-testid="matrix-legend"
      >
        <Tag color="green">Direct</Tag>
        <Tag color="blue">Role</Tag>
        <Tag color="purple">Group</Tag>
      </div>
    </div>
  )
}
